import { apiClient } from '@/api/client'

/**
 * True for the WebKit "no active service worker yet" error (DOMException, name
 * `InvalidStateError`, code 11). iOS standalone PWAs throw this from `pushManager`
 * right after launch (before `registration.active` is set). It's benign — treat it
 * as "no subscription" so it never bubbles up as an unhandled rejection into Sentry.
 */
function isInvalidStateError(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'InvalidStateError' || err.code === 11)
}

/**
 * `pushManager.getSubscription()` that tolerates a not-yet-active worker: returns
 * null when there's no active worker (iOS standalone right after launch) or when
 * WebKit throws InvalidStateError, instead of rejecting.
 */
async function safeGetSubscription(
  registration: ServiceWorkerRegistration | null | undefined,
): Promise<PushSubscription | null> {
  if (!registration) return null
  // Explicitly no active worker → pushManager would throw; short-circuit. (=== null,
  // not falsy, so a test/mock registration without an `active` field still probes.)
  if (registration.active === null) return null
  try {
    return await registration.pushManager.getSubscription()
  } catch (err) {
    if (isInvalidStateError(err)) return null
    throw err
  }
}

/** True if this browser supports service workers and the Push API. */
export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** Returns the current notification permission state. */
export function getPermission(): NotificationPermission {
  return typeof Notification !== 'undefined' ? Notification.permission : 'denied'
}

/** Asks the user for notification permission. Returns the resulting state. */
export async function requestPushPermission(): Promise<NotificationPermission> {
  return Notification.requestPermission()
}

/**
 * Converts a base64url-encoded string to a Uint8Array.
 * Required to pass the VAPID application server key to pushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const buffer = new ArrayBuffer(rawData.length)
  const view = new Uint8Array(buffer)
  for (let i = 0; i < rawData.length; i++) view[i] = rawData.charCodeAt(i)
  return view
}

/**
 * Registers the service worker and subscribes to push notifications.
 * Returns the PushSubscription object, or null if anything fails.
 */
export async function subscribeToPush(applicationServerKey: string): Promise<PushSubscription | null> {
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready

    const existing = await registration.pushManager.getSubscription()
    if (existing) {
      // Check if the existing subscription was created with the same application server key.
      // If the server's VAPID keys have been regenerated, the existing subscription's key
      // won't match and sending push will fail with 403. In that case, unsubscribe and
      // re-subscribe with the current key.
      const expectedKey = urlBase64ToUint8Array(applicationServerKey)
      const existingKey = existing.options?.applicationServerKey
        ? new Uint8Array(existing.options.applicationServerKey)
        : null

      if (existingKey && expectedKey.length === existingKey.length &&
          expectedKey.every((b, i) => b === existingKey[i])) {
        return existing // keys match — reuse
      }

      // Key mismatch — unsubscribe stale subscription and create fresh one
      console.info('VAPID key mismatch detected — re-subscribing with current server key')
      await existing.unsubscribe()
    }

    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(applicationServerKey),
    })
  } catch (err) {
    // InvalidStateError (no active worker yet, iOS standalone launch) is benign.
    if (isInvalidStateError(err)) console.debug('Push subscribe skipped — no active worker yet')
    else console.error('Push subscription failed:', err)
    return null
  }
}

/** Returns the current push subscription if one exists, otherwise null. */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js')
    return await safeGetSubscription(registration)
  } catch {
    return null
  }
}

/** Unsubscribes from push notifications in the browser. */
export async function unsubscribeFromPush(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js')
    const sub = await safeGetSubscription(registration)
    if (sub) await sub.unsubscribe()
  } catch (err) {
    console.error('Push unsubscribe failed:', err)
  }
}

/**
 * True if the CURRENT account already has at least one push subscription
 * registered on the backend — i.e. it previously opted into push. Used by the
 * self-heal path to avoid auto-subscribing a brand-new account. On any error we
 * return false (fail closed: never auto-subscribe when prior opt-in is unknown).
 */
async function accountHasServerSubscription(): Promise<boolean> {
  try {
    const { data } = await apiClient.get<unknown[]>('/push/subscriptions')
    return Array.isArray(data) && data.length > 0
  } catch {
    return false
  }
}

/** Fetches the org's VAPID application server key. Returns null if not configured. */
async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const { data } = await apiClient.get<{ publicKey: string; enabled: string }>('/push/vapid-public-key')
    return data.publicKey && data.enabled === 'true' ? data.publicKey : null
  } catch {
    console.error('Failed to fetch VAPID public key')
    return null
  }
}

/**
 * Subscribes to push and registers the subscription with the backend.
 * VAPID keys are auto-provisioned at org registration so the public key
 * always exists by the time the user lands on this toggle — no bootstrap
 * dance required. If applicationServerKey is provided it's used directly;
 * otherwise fetched from server.
 * Returns true on success.
 */
export async function enablePushNotifications(applicationServerKey?: string): Promise<boolean> {
  const permission = await requestPushPermission()
  if (permission !== 'granted') return false

  const key = applicationServerKey || await fetchVapidPublicKey()
  if (!key) return false

  const sub = await subscribeToPush(key)
  if (!sub) return false

  const keys = sub.toJSON().keys ?? {}
  await apiClient.post('/push/subscribe', {
    endpoint: sub.endpoint,
    p256dhKey: keys.p256dh ?? '',
    authKey: keys.auth ?? '',
    userAgent: navigator.userAgent,
  })
  return true
}

/**
 * Silent, no-prompt repair of a dropped push subscription, run on app launch.
 * If the user already granted permission but the browser subscription has
 * vanished (iOS silently drops it across OS updates / storage eviction), this
 * re-subscribes and re-registers with the backend — invisibly. NEVER prompts:
 * if permission isn't already 'granted', it does nothing. Best-effort/silent.
 *
 * Only heals accounts that PREVIOUSLY enabled push on this account (the backend
 * has a subscription record for them). A brand-new account on a browser that
 * already holds 'granted' permission is left unsubscribed until it explicitly
 * opts in via PersonalPushToggle / PWAOnboardingBanner.
 */
export async function ensurePushSubscription(): Promise<void> {
  if (!isPushSupported() || getPermission() !== 'granted') return
  try {
    const existing = await getCurrentSubscription()
    if (existing) return            // healthy — zero network, nothing to do

    // Only HEAL accounts that previously enabled push on this account. A
    // brand-new account signing in on a browser that already holds 'granted'
    // permission (e.g. a different account used this device before) must NOT be
    // auto-subscribed — that's an opt-in this account never made. The backend's
    // per-account subscription records are the durable "was previously enabled
    // for THIS account" signal: they survive the iOS storage eviction that drops
    // the browser sub, and are empty for a fresh account.
    if (!(await accountHasServerSubscription())) return

    const key = await fetchVapidPublicKey()
    if (!key) return
    const sub = await subscribeToPush(key)
    if (!sub) return
    const keys = sub.toJSON().keys ?? {}
    await apiClient.post('/push/subscribe', {
      endpoint: sub.endpoint,
      p256dhKey: keys.p256dh ?? '',
      authKey: keys.auth ?? '',
      userAgent: navigator.userAgent,
    })
  } catch {
    // best-effort; never surface to the user
  }
}

/**
 * Unsubscribes from push notifications and removes the subscription from the backend.
 */
export async function disablePushNotifications(): Promise<void> {
  // Read the endpoint defensively: the getSubscription() here was previously
  // unguarded, so an InvalidStateError (iOS standalone, no active worker) became an
  // unhandled rejection → Sentry noise on /dashboard. safeGetSubscription tolerates it.
  let endpoint: string | undefined
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js')
    const sub = await safeGetSubscription(registration)
    endpoint = sub?.endpoint
  } catch {
    endpoint = undefined
  }

  await unsubscribeFromPush()

  if (endpoint) {
    try {
      // endpoint as a query param — DELETE bodies are dropped by the prod proxy chain
      await apiClient.delete('/push/unsubscribe', { params: { endpoint } })
    } catch {
      // Best-effort: subscription already cleaned up on the browser side
    }
  }
}
